import React, { createContext, useCallback, useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useCreateOrder } from './useCreateOrder'
import { FROM_HOMEPAGE } from 'settings/_scheduledFrom'
import { GPR_CONSULT } from 'settings/_productSettings'
import ReservationService from 'services/reservation'
import { ProfessionalRestrictions } from 'services/reservation/professionalRestrictions'
import { PatientCreatingReservationRestrictionsModal } from 'containers/ModalManager/Modals/PatientCreatingReservationRestrictionsModal'
import type { PatientCreatingReservationConfirmPayload } from 'containers/ModalManager/Modals/PatientCreatingReservationRestrictionsModal'
import * as routes from 'settings/_routesSettings'
import {
  buildSelectedNonRecurrentReservationPairs,
  toReservationCheckoutExceptions,
} from 'utils/toReservationCheckoutExceptions'
import type { ReservationCheckoutExceptionItem } from 'utils/toReservationCheckoutExceptions'
import { invalidatePatientReservationCardCache } from 'utils/invalidatePatientReservationCardCache'

type QuickSchedule = {
  startDate: Date
  endDate: Date
  professionalId: number
  professionalUuid: string
  professionalUrl: string
  professionalType: 'PSIC' | 'PSIQ'
  professionalAppointmentId: number
  autoReservationEnabled?: boolean
  isRecurring?: boolean
}

interface QuickScheduleContextData {
  quickSchedule: QuickSchedule
  setQuickSchedule: (quickSchedule: QuickSchedule) => void
  handleCreateOrder: () => void
  isCreateOrderLoading: boolean
  clearQuickSchedule: () => void
}

interface AuthData {
  auth: {
    userData: {
      person_profile: {
        id: number
      }[]
      username: string
    }
    planSelected: {
      partner_plan_code: string
    }
  }
}

export const QuickScheduleContext = createContext<QuickScheduleContextData>({} as QuickScheduleContextData)

export const QuickScheduleProvider: React.FC = ({ children }) => {
  const { onCreateOrder, isCreateOrderLoading } = useCreateOrder()
  const history = useHistory()
  const queryClient = useQueryClient()

  const [quickSchedule, setQuickSchedule] = useState<QuickSchedule>(null)
  const [isReservationFlowLoading, setIsReservationFlowLoading] = useState(false)
  const [showRestrictionsReservationModal, setShowRestrictionsReservationModal] = useState(false)
  const [restrictions, setRestrictions] = useState<Date[]>([])
  const [pendingReservation, setPendingReservation] = useState<ProfessionalRestrictions | null>(null)
  const [nonRecurrentReservationDates, setNonRecurrentReservationDates] = useState<string[]>([])
  const userData = useSelector((state: AuthData) => state.auth.userData)
  const patientId: number = userData?.person_profile[0].id
  const username: string = userData?.username
  const planCode: string = useSelector((state: AuthData) => state.auth.planSelected?.partner_plan_code)
  const payload = {
    product_group_code: GPR_CONSULT,
    plan_code: planCode,
    profile_role: quickSchedule?.professionalType,
    person_profile_product_appointment_id: quickSchedule?.professionalAppointmentId,
    username: null,
    schedules: [{
      start_date: quickSchedule?.startDate.toISOString(),
      end_date: quickSchedule?.endDate.toISOString(),
      ...(quickSchedule?.isRecurring !== undefined && {
        is_recurring: quickSchedule.isRecurring,
      }),
      guests: [
        {
          person_profile_id: quickSchedule?.professionalId
        },
        {
          person_profile_id: patientId
        }
      ]
    }],
    scheduledFrom: FROM_HOMEPAGE,
    autoReservationEnabled: quickSchedule?.autoReservationEnabled,
  }

  const goToPlanCheckoutAfterReservation = useCallback((
    reservationConfirmationSucceeded: boolean,
    reservationNonRecurrentExceptions: ReservationCheckoutExceptionItem[] | undefined
  ) => {
    history.push({
      pathname: routes.PLAN_CHECKOUT,
      state: {
        scheduledFrom: FROM_HOMEPAGE,
        reservationConfirmationSucceeded,
        ...(reservationNonRecurrentExceptions?.length && {
          reservationNonRecurrentExceptions,
        }),
      },
    })
  }, [history])

  async function handleCreateOrder() {
    setIsReservationFlowLoading(true)
    try {
      const orderCreated = await onCreateOrder(payload)
      if (!orderCreated) return

      if (
        quickSchedule?.autoReservationEnabled &&
        quickSchedule?.professionalType === 'PSIC' &&
        quickSchedule?.isRecurring !== false
      ) {
        try {
          const patientRestrictionsResponse = await ReservationService.patientRestrictions({
            username: username,
            planCode: planCode
          })

          if (patientRestrictionsResponse.restrictionStatus === 'BLOCKED') {
            goToPlanCheckoutAfterReservation(false, undefined)
            return
          }

          const professionalRestrictionsResponse = await ReservationService.professionalRestrictions({
            professionalUsername: quickSchedule.professionalUrl,
            patientUsername: username,
            startDate: quickSchedule.startDate,
            endDate: quickSchedule.endDate,
            planCode: planCode
          })

          if (professionalRestrictionsResponse.restrictions && professionalRestrictionsResponse.restrictions.length > 0) {
            setRestrictions(professionalRestrictionsResponse.restrictions)
            setPendingReservation(professionalRestrictionsResponse)
            setShowRestrictionsReservationModal(true)
            return
          }

          const reservationResult = await ReservationService.createReservation({
            professionalUuid: quickSchedule.professionalUuid,
            patientUsername: username,
            reservationStartDate: professionalRestrictionsResponse.reservationStartDate,
            reservationEndDate: professionalRestrictionsResponse.reservationEndDate,
            recurrent: true
          })
          invalidatePatientReservationCardCache(queryClient)
          goToPlanCheckoutAfterReservation(
            true,
            toReservationCheckoutExceptions(undefined, reservationResult?.nonRecurrentReservations?.successful)
          )
        } catch {
          goToPlanCheckoutAfterReservation(false, undefined)
        }
      }
    } finally {
      setIsReservationFlowLoading(false)
    }
  }

  function clearQuickSchedule() {
    setQuickSchedule(null)
  }

  const handleNonRecurrentReservationDate = useCallback((date: string | null, slotIndex: number) => {
    setNonRecurrentReservationDates((previous) => {
      const next = [...previous]
      next[slotIndex] = date
      return next
    })
  }, [])

  const hasNonRecurrentReservationDates = nonRecurrentReservationDates
    .every(date => Boolean(date)) && nonRecurrentReservationDates.length > 0

  const shouldDisableConfirmRestrictionsModal = restrictions.length > 0 && !hasNonRecurrentReservationDates

  const handleConfirmRestrictionsReservationModal = useCallback(async (
    payload?: PatientCreatingReservationConfirmPayload
  ) => {
    if (!pendingReservation) return
    if (payload?.skipCreateReservation) {
      setShowRestrictionsReservationModal(false)
      setRestrictions([])
      setPendingReservation(null)
      setNonRecurrentReservationDates([])
      goToPlanCheckoutAfterReservation(false, undefined)
      return
    }
    if (shouldDisableConfirmRestrictionsModal) return
    if (!quickSchedule?.professionalUuid) return

    try {
      const selectedPairs = buildSelectedNonRecurrentReservationPairs(
        nonRecurrentReservationDates,
        restrictions
      )
      const reservationResult = await ReservationService.createReservation({
        professionalUuid: quickSchedule.professionalUuid,
        patientUsername: username,
        reservationStartDate: pendingReservation.reservationStartDate,
        reservationEndDate: pendingReservation.reservationEndDate,
        recurrent: true,
        nonRecurrentReservations:
          selectedPairs.length > 0 ? selectedPairs.map((item) => item.interval) : null,
      })
      invalidatePatientReservationCardCache(queryClient)
      setShowRestrictionsReservationModal(false)
      setRestrictions([])
      setPendingReservation(null)
      setNonRecurrentReservationDates([])
      goToPlanCheckoutAfterReservation(
        true,
        toReservationCheckoutExceptions(
          selectedPairs.map((item) => item.restrictionDate),
          reservationResult?.nonRecurrentReservations?.successful
        )
      )
    } catch {
      goToPlanCheckoutAfterReservation(false, undefined)
    }
  }, [
    pendingReservation,
    quickSchedule,
    shouldDisableConfirmRestrictionsModal,
    nonRecurrentReservationDates,
    restrictions,
    username,
    queryClient,
    goToPlanCheckoutAfterReservation,
  ])

  const handleCloseRestrictionsReservationModal = useCallback(() => {
    setShowRestrictionsReservationModal(false)
    setRestrictions([])
    setPendingReservation(null)
    setNonRecurrentReservationDates([])
    goToPlanCheckoutAfterReservation(false, undefined)
  }, [goToPlanCheckoutAfterReservation])

  return (
    <QuickScheduleContext.Provider value={{
      quickSchedule,
      setQuickSchedule,
      handleCreateOrder,
      isCreateOrderLoading: isCreateOrderLoading || isReservationFlowLoading,
      clearQuickSchedule
    }}>
      {children}
      {showRestrictionsReservationModal && (
        <PatientCreatingReservationRestrictionsModal
          onClose={handleCloseRestrictionsReservationModal}
          restrictions={restrictions}
          patientUsername={username}
          professionalUsername={quickSchedule?.professionalUrl ?? ''}
          onSelectNonRecurrentReservationDate={handleNonRecurrentReservationDate}
          onConfirm={handleConfirmRestrictionsReservationModal}
          confirmDisabled={shouldDisableConfirmRestrictionsModal}
        />
      )}
    </QuickScheduleContext.Provider>
  )
}

export function useQuickSchedule(): QuickScheduleContextData {
  const context = useContext(QuickScheduleContext)

  return context
}
